import { useMemo, useState, useEffect } from "react";
import { Search, Calendar, ShoppingBag, FileText } from "lucide-react";
import DataTable, { TableColumn } from "react-data-table-component";
import { RevenueSummaryByBrandItem } from "../../../services/revenueservice";
import {
  cx,
  formatCurrency,
  MONTHS_OPTIONS,
  YEAR_OPTIONS,
} from "../../../utils";
import { useBrandTable } from "../../../hooks/useBrandTable";

const tableStyles = {
  light: {
    headRow: {
      style: {
        backgroundColor: "#f9fafb",
        borderBottomWidth: "1px",
        borderBottomColor: "#e5e7eb",
        minHeight: "44px",
      },
    },
    headCells: {
      style: {
        fontSize: "0.75rem",
        fontWeight: "600",
        textTransform: "uppercase" as const,
        letterSpacing: "0.05em",
        color: "#6b7280",
        paddingLeft: "1.5rem",
        paddingRight: "1.5rem",
      },
    },
    rows: {
      style: { fontSize: "0.875rem", color: "#1f2937", minHeight: "52px" },
      highlightOnHoverStyle: {
        backgroundColor: "#f3f4f6",
        borderBottomColor: "#e5e7eb",
        outline: "none",
      },
    },
    cells: {
      style: { paddingLeft: "1.5rem", paddingRight: "1.5rem" },
    },
    pagination: {
      style: {
        borderTopWidth: "1px",
        borderTopColor: "#e5e7eb",
        fontSize: "0.875rem",
        color: "#374151",
      },
    },
    noData: {
      style: {
        backgroundColor: "#ffffff",
      },
    },
  },
  dark: {
    headRow: {
      style: {
        backgroundColor: "#374151",
        borderBottomWidth: "1px",
        borderBottomColor: "#4b5563",
        minHeight: "44px",
      },
    },
    headCells: {
      style: {
        fontSize: "0.75rem",
        fontWeight: "600",
        textTransform: "uppercase" as const,
        letterSpacing: "0.05em",
        color: "#d1d5db",
        paddingLeft: "1.5rem",
        paddingRight: "1.5rem",
      },
    },
    rows: {
      style: {
        fontSize: "0.875rem",
        backgroundColor: "#1f2937",
        color: "#e5e7eb",
        minHeight: "52px",
      },
      highlightOnHoverStyle: {
        backgroundColor: "#374151",
        borderBottomColor: "#4b5563",
        outline: "none",
      },
    },
    cells: {
      style: { paddingLeft: "1.5rem", paddingRight: "1.5rem" },
    },
    pagination: {
      style: {
        backgroundColor: "#1f2937",
        color: "#d1d5db",
        borderTopWidth: "1px",
        borderTopColor: "#4b5563",
        fontSize: "0.875rem",
      },
    },
    noData: {
      style: {
        backgroundColor: "#1f2937",
      },
    },
  },
};

function FilterLabel({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-white">
      {icon}
      {text}
    </label>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 bg-white dark:bg-gray-800 w-full">
      <div className="w-16 h-16 bg-gray-50 dark:bg-gray-700 rounded-full flex items-center justify-center mb-3">
        <FileText className="w-8 h-8 text-gray-300 dark:text-gray-500" />
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>
    </div>
  );
}

export default function BrandTable() {
  const {
    mallOptions,
    selectedMall,
    isMallDisabled,
    selectedMonth,
    setSelectedMonth,
    selectedYear,
    setSelectedYear,
    searchQuery,
    setSearchQuery,
    filteredData,
    isLoading,
    totalAmount,
    totalTransactions,
    emptyMessage,
    currentPageRef,
    rowsPerPageRef,
    handlePageChange,
    handleRowsPerPageChange,
  } = useBrandTable();

  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("dark"),
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  const hasMonthSelected = Boolean(selectedMonth);

  const columns: TableColumn<RevenueSummaryByBrandItem>[] = useMemo(
    () => [
      {
        name: "No",
        width: "70px",
        cell: (_row, index) => (
          <span className="text-gray-500 dark:text-gray-400">
            {(currentPageRef.current - 1) * rowsPerPageRef.current + index + 1}
          </span>
        ),
      },
      {
        name: "Mall Name",
        selector: (row) => row.MallName,
        sortable: true,
        grow: 2,
        cell: (row) => (
          <span className="font-medium text-gray-800 dark:text-gray-200">
            {row.MallName}
          </span>
        ),
      },
      {
        name: "Amount (Rp)",
        selector: (row) => row.Amount,
        sortable: true,
        right: true,
        cell: (row) => (
          <span className="text-gray-800 dark:text-gray-200">
            {formatCurrency(row.Amount)}
          </span>
        ),
      },
      {
        name: "Total Transaction",
        selector: (row) => row.TotalData,
        sortable: true,
        right: true,
        cell: (row) => (
          <span className="text-gray-800 dark:text-gray-200">
            {Number(row.TotalData).toLocaleString("id-ID")}
          </span>
        ),
      },
    ],
    [currentPageRef, rowsPerPageRef],
  );

  const selectClass = (disabled: boolean) =>
    cx(
      "w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white",
      disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
    );

  return (
    <div className="min-h-screen">
      <div className="w-full max-w-7xl space-y-4">
        <div className="bg-white rounded-lg shadow overflow-hidden dark:bg-gray-800">
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <FilterLabel
                  icon={<ShoppingBag className="w-4 h-4 text-indigo-600" />}
                  text="Mall"
                />
                <select
                  value={selectedMall}
                  disabled={isMallDisabled}
                  onChange={() => { }}
                  className={selectClass(isMallDisabled)}
                >
                  <option value="">-- Choose One --</option>
                  {mallOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <FilterLabel
                  icon={<Calendar className="w-4 h-4 text-indigo-600" />}
                  text="Month"
                />
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className={selectClass(false)}
                >
                  <option value="">-- Choose Month --</option>
                  {MONTHS_OPTIONS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <FilterLabel
                  icon={<Calendar className="w-4 h-4 text-indigo-600" />}
                  text="Year"
                />
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className={selectClass(false)}
                >
                  <option value="">-- Choose Year --</option>
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {hasMonthSelected ? (
          <div className="bg-white rounded-lg shadow overflow-hidden dark:bg-gray-800">
            <div className="flex justify-end p-3 sm:p-4 border-b border-gray-100 dark:border-gray-700">
              <div className="relative w-full sm:max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                />
              </div>
            </div>

            <DataTable<RevenueSummaryByBrandItem>
              columns={columns}
              data={filteredData}
              progressPending={isLoading}
              progressComponent={
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
                  <p className="text-sm text-gray-500">Loading data...</p>
                </div>
              }
              noDataComponent={<EmptyState message={emptyMessage} />}
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              highlightOnHover
              responsive
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handleRowsPerPageChange}
              customStyles={isDark ? tableStyles.dark : tableStyles.light}
            />

            {!isLoading && filteredData.length > 0 && (
              <div className="grid grid-cols-2 px-6 py-3 bg-gray-50 dark:bg-gray-700 border-t-2 border-gray-200 dark:border-gray-600 text-sm font-bold text-gray-700 dark:text-gray-200">
                <span>Total ({filteredData.length} records)</span>
                <div className="flex flex-col items-end gap-0.5">
                  <span>Rp {formatCurrency(totalAmount)}</span>
                  <span className="font-medium text-gray-500 dark:text-gray-400">
                    {totalTransactions.toLocaleString("id-ID")} transaksi
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow flex flex-col items-center justify-center py-12 sm:py-16 gap-3">
            <div className="w-16 h-16 bg-gray-50 dark:bg-gray-700 rounded-full flex items-center justify-center">
              <Calendar className="w-8 h-8 text-gray-300" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Please select a month to view data</p>
          </div>
        )}
      </div>
    </div>
  );
}